// Minimal observable store. No framework, no dependencies.
// Subscribers are called on every update; the UI re-renders what it needs.

import type { Appointment, FinderConfig } from './types';
import { defaultConfig } from './logic';

export type State = {
  config: FinderConfig;
  appointments: Appointment[];
};

type Listener = (state: State) => void;

export class Store {
  private state: State;
  private listeners = new Set<Listener>();

  constructor(initial?: Partial<State>) {
    this.state = {
      config: initial?.config ?? defaultConfig(),
      appointments: initial?.appointments ?? [],
    };
  }

  get(): State {
    return this.state;
  }

  setConfig(config: FinderConfig): void {
    this.state = { ...this.state, config };
    this.emit();
  }

  patchConfig(patch: Partial<FinderConfig>): void {
    this.state = { ...this.state, config: { ...this.state.config, ...patch } };
    this.emit();
  }

  setAppointments(appointments: Appointment[]): void {
    this.state = { ...this.state, appointments };
    this.emit();
  }

  addAppointment(appointment: Appointment): void {
    this.state = { ...this.state, appointments: [...this.state.appointments, appointment] };
    this.emit();
  }

  removeAppointment(id: string): void {
    this.state = {
      ...this.state,
      appointments: this.state.appointments.filter((a) => a.id !== id),
    };
    this.emit();
  }

  /** Aktualisiert ein einzelnes Feld auf einem Termin (z.B. location). */
  updateAppointment(id: string, patch: Partial<Appointment>): void {
    this.state = {
      ...this.state,
      appointments: this.state.appointments.map((a) =>
        a.id === id ? { ...a, ...patch } : a,
      ),
    };
    this.emit();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    for (const l of this.listeners) l(this.state);
  }
}
